import type { LocalDate, PersoConflict, PersoEvent } from '@ergo/shared';
import { api } from './api';
import { useResource } from './hooks';
import {
  APP_TIME_ZONE,
  addDays,
  dateOf,
  formatFrenchTime,
  formatShortDate,
  isValidDate,
  minutesOfTime,
  timeOf,
  today,
} from './time';

/**
 * Son agenda Google sur la fenêtre affichée. Une erreur ne remonte pas : les
 * rendez-vous perso sont un décor utile, jamais une raison d'abîmer l'écran —
 * Réglages est le seul endroit qui dit qu'une synchro s'est mal passée.
 */
export function usePersoEvents(from: LocalDate, to: LocalDate): PersoEvent[] {
  const resource = useResource(
    () =>
      isValidDate(from) && isValidDate(to)
        ? api.perso.list({ from, to }).catch(() => ({ events: [], fetchedAt: null, error: null }))
        : Promise.resolve({ events: [], fetchedAt: null, error: null }),
    [from, to],
  );
  return resource.data?.events ?? [];
}

/** Les rendez-vous d'un jour donné : ceux à l'heure, et les journées entières. */
export function persoOfDay(
  events: PersoEvent[],
  date: LocalDate,
): { timed: PersoEvent[]; allDay: PersoEvent[] } {
  const from = `${date}T00:00`;
  const until = `${addDays(date, 1)}T00:00`;
  const touching = events.filter((event) => event.startsAt < until && event.endsAt > from);

  return {
    timed: touching.filter((event) => !event.allDay),
    allDay: touching.filter((event) => event.allDay),
  };
}

/**
 * Le début et la fin d'un rendez-vous perso ramenés au jour affiché : un rendez-vous
 * qui déborde sur le lendemain s'arrête en bas de la colonne, pas plus loin.
 */
export function clampToDay(
  event: PersoEvent,
  date: LocalDate,
): { startMin: number; endMin: number } {
  const startMin = dateOf(event.startsAt) === date ? minutes(event.startsAt) : 0;
  const endMin = dateOf(event.endsAt) === date ? minutes(event.endsAt) : 24 * 60;
  return { startMin, endMin: Math.max(endMin, startMin + 15) };
}

function minutes(dateTime: string): number {
  return minutesOfTime(timeOf(dateTime));
}

/** « Chevauche « Dentiste » » · « Proche de « Dentiste » (14 h 00) » */
export function conflictLabel({ event, overlaps }: PersoConflict): string {
  return overlaps
    ? `Chevauche « ${event.title} »`
    : `Proche de « ${event.title} » (${formatFrenchTime(timeOf(event.startsAt))})`;
}

/** « 14 h 00 » — l'heure comme elle se dit dans une phrase. */
export { formatFrenchTime as frenchTime };

/**
 * Quand remonte la dernière synchro : « à 14 h 02 » dans la journée, « le 05/09 à
 * 14 h 02 » au-delà. C'est le seul instant vrai que l'app affiche — d'où la
 * conversion, tout le reste étant déjà en heure locale.
 */
export function formatSyncMoment(iso: string): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return '';

  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(instant);
  const date = parts.slice(0, 10);
  const time = formatFrenchTime(parts.slice(11, 16));

  return date === today() ? `à ${time}` : `le ${formatShortDate(date)} à ${time}`;
}
